import { cn } from '@/lib/cn'
import { Breadcrumb, type BreadcrumbItem } from '@/components/common/Breadcrumb'
import { PageHeader, type PageHeaderProps } from '@/components/common/PageHeader'

export interface PageShellProps
  extends Pick<PageHeaderProps, 'icon' | 'title' | 'description' | 'actions'> {
  /** Breadcrumb trail; current page is last item (omit `to`). */
  breadcrumbItems: BreadcrumbItem[]
  /** Optional content between breadcrumb and page title (e.g. company sub-nav). */
  belowBreadcrumb?: React.ReactNode
  pageHeaderClassName?: string
  /**
   * Hide the in-page header (breadcrumb + icon tile + title + description) when the route
   * supplies its own compact workspace chrome (e.g. a flush toolbar). Navigation context is
   * still shown as "Section › Page" in the top header bar (see `config/pageBreadcrumb`), so no
   * vertical space is spent on a separate breadcrumb row.
   */
  hideHeader?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * Standard page frame: breadcrumb → optional strip → title row → main content.
 * Keeps hierarchy and reading order consistent and avoids duplicate layout patterns.
 */
export function PageShell({
  breadcrumbItems,
  belowBreadcrumb,
  icon,
  title,
  description,
  actions,
  pageHeaderClassName,
  hideHeader = false,
  className,
  children,
}: PageShellProps): React.ReactElement {
  return (
    <div className={cn('flex min-w-0 flex-col gap-8', className)}>
      {!hideHeader && (
        <header className="shrink-0 space-y-6">
          <Breadcrumb items={breadcrumbItems} />
          {belowBreadcrumb}
          <PageHeader
            icon={icon}
            title={title}
            description={description}
            actions={actions}
            className={pageHeaderClassName}
          />
        </header>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  )
}

PageShell.displayName = 'PageShell'
